// Owner 2026-09-29: the phone preview card («jak na PL», product page) folds into a small button so it does not
// cover the page. The card stays as it was while it helps and where it sits at its own place above the steps:
//   full  intro and steps 1–2 while the options of the open step still show under the card;
//   chip  the heel step whenever the card is stuck under the header, and steps 1–2 once their options have scrolled
//         under the card (the landing below, the add-to-cart button).
// The card leaves the flow but the wrapper keeps its height (--evc-reserve), so nothing below moves. Tapping the
// button opens the card again as an overlay with the big render (peek); «Skrýt», a step change, Esc or scrolling
// ~90 px on folds it back. State is kept in data-evc-mode / data-evc-peek only: the classes belong to
// product-config.js (configImagePreview), which is not touched. The look is in evamats-card-chip.css.
(() => {
  const PHONE = window.matchMedia('(max-width: 989px)');
  const STUCK_TOL = 1; // px: at or above the stick line means the card is stuck under the header
  const RELEASE_AT = 24; // px below the stick line means the card is back at its own place
  const HIDE_BELOW = 12; // px of the open step still showing under the card: at or below this the card folds
  const SHOW_ABOVE = 72; // ...and comes back once this much of the step shows again
  const PEEK_SCROLL = 90; // px of scrolling after which an opened preview folds back into the button
  const STEPS = {
    set: '[data-dropdown="mats_set_type"]',
    colors: '[data-dropdown="mat_patterns_colors"]',
    heel: '.product__dropdown_wr--heel'
  };

  function init(wrap) {
    if (wrap.dataset.evcChipReady) return;
    const card = wrap.querySelector('.evc-card');
    const chip = wrap.querySelector('[data-evc-chip]');
    const toggle = wrap.querySelector('[data-preview-expand]');
    const stage = wrap.querySelector('.evc-card__stage');
    if (!card || !chip || !toggle) return;
    wrap.dataset.evcChipReady = '1';

    const scope = wrap.closest('.evamats-config') || document;
    let mode = wrap.getAttribute('data-evc-mode') === 'chip' ? 'chip' : 'full';
    let peek = false;
    let peekY = 0;
    let frame = 0;
    let waits = 0;
    let foldedHeight = 0; // the wrapper's height with the render folded, remembered while it is folded
    let userClosed = false; // «Skrýt» or Esc: the visitor closed the preview (Safari does not focus a button on click)

    const expanded = () => wrap.classList.contains('is-expanded');
    // The big render changes the card's height, so the card may only leave or join the flow when it is folded.
    const stageOpen = () => Boolean(stage) && stage.getBoundingClientRect().height > 0.5;
    const openStep = () => Object.keys(STEPS).find((key) => {
      const el = scope.querySelector(STEPS[key]);
      return Boolean(el && el.classList.contains('open'));
    }) || '';
    const padding = () => {
      const cs = getComputedStyle(wrap);
      return (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(sync); };

    function applyMode(next) {
      mode = next;
      wrap.setAttribute('data-evc-mode', next);
      card.inert = next === 'chip';
      chip.inert = next !== 'chip';
    }

    function setMode(next) {
      if (next === mode) return;
      const hadFocus = card.contains(document.activeElement);
      // the wrapper's folded height, taken while the card is still in the flow
      if (next === 'chip') wrap.style.setProperty('--evc-reserve', wrap.getBoundingClientRect().height + 'px');
      applyMode(next);
      if (next === 'chip' && hadFocus) chip.focus({ preventScroll: true });
    }

    function openPeek() {
      peek = true;
      userClosed = false;
      peekY = window.scrollY;
      wrap.setAttribute('data-evc-peek', '');
      applyMode('full');
      if (!expanded()) toggle.click();
      userClosed = false;
      toggle.focus({ preventScroll: true });
    }

    function closePeek() {
      if (!peek) return;
      // focus goes back to the button when the visitor closed the preview or focus was inside the card; a fold caused
      // by scrolling or a step change does not pull focus
      const hadFocus = userClosed || card.contains(document.activeElement);
      userClosed = false;
      peek = false;
      wrap.removeAttribute('data-evc-peek');
      applyMode('chip'); // straight back to the button: the card stays out of the flow while its render folds
      if (expanded()) toggle.click();
      if (hadFocus) chip.focus({ preventScroll: true });
      schedule();
    }

    function reset() {
      waits = 0;
      if (peek) { peek = false; wrap.removeAttribute('data-evc-peek'); }
      if (mode !== 'full') applyMode('full');
    }

    // What the card should be right now: full while it sits at its own place or the open step's options are still
    // visible under it, the button on the heel step and once the options have gone under the card.
    function decide() {
      const rect = wrap.getBoundingClientRect();
      if (!expanded() && !stageOpen()) foldedHeight = rect.height;
      // An opened render makes the card several times taller; the options «went under» it by the folded card's
      // measure, so a card the visitor opened stays open while they work in the step and folds once they leave it.
      const bottom = rect.top + (foldedHeight || rect.height);
      const stick = parseFloat(getComputedStyle(wrap).top) || 0;
      const atOwnPlace = mode === 'chip' ? rect.top >= stick + RELEASE_AT : rect.top > stick + STUCK_TOL;
      if (atOwnPlace) return 'full';
      const key = openStep();
      if (key === 'heel') return 'chip';
      const step = key && scope.querySelector(STEPS[key]);
      if (!step) return 'full';
      const left = step.getBoundingClientRect().bottom - bottom;
      if (mode === 'chip') return left > SHOW_ABOVE ? 'full' : 'chip';
      return left < HIDE_BELOW ? 'chip' : 'full';
    }

    function sync() {
      frame = 0;
      if (!PHONE.matches || !wrap.classList.contains('is-card-visible')) { reset(); return; }
      if (peek) {
        if (!expanded() || Math.abs(window.scrollY - peekY) > PEEK_SCROLL) closePeek();
        return;
      }
      const next = decide();
      if (next === mode) { waits = 0; return; }
      if (stageOpen() && waits < 12) {
        // the owner scrolled on with the render open: fold it with the card's own toggle, then swap once it is shut
        if (next === 'chip' && expanded()) toggle.click();
        waits += 1;
        setTimeout(schedule, 80);
        return;
      }
      waits = 0;
      setMode(next);
    }

    chip.addEventListener('click', (event) => {
      event.preventDefault();
      openPeek();
    });
    toggle.addEventListener('click', (event) => { if (peek && event.isTrusted) userClosed = true; }); // our own toggle.click() is not the visitor
    wrap.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && peek) { userClosed = true; closePeek(); }
    });

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    document.addEventListener('evamats:config-step-updated', schedule);
    new MutationObserver(schedule).observe(wrap, { attributes: true, attributeFilter: ['class'] });
    scope.querySelectorAll('.product__dropdown_wr, [data-config-intro]').forEach((element) => {
      new MutationObserver(schedule).observe(element, { attributes: true, attributeFilter: ['class'] });
    });
    // text of a tile changed or the width did: keep the folded height in step with the card
    if ('ResizeObserver' in window) {
      new ResizeObserver(() => {
        if (mode === 'chip' && !peek && !stageOpen()) wrap.style.setProperty('--evc-reserve', card.offsetHeight + padding() + 'px');
      }).observe(card);
    }
    schedule();
  }

  const start = () => {
    document.querySelectorAll('.evamats-config .evamats-config-preview--card[data-evc-card]').forEach(init);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  document.addEventListener('shopify:section:load', start);
})();
